<template>
  <div
    class="MediaBankCard relative bg-bg-base border border-border-subtle group cursor-pointer"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
  >
    <!-- Thumbnail -->
    <div class="relative aspect-square overflow-hidden bg-lighter">
      <template v-if="asset.mediaType === 'Images'">
        <!-- Use nuxt-img for real Norce CDN URLs; plain img for external/mock URLs -->
        <nuxt-img
          v-if="isNorceUrl && !imageError"
          :src="asset.thumbnailUrl"
          provider="norce"
          preset="plpimage"
          sizes="mob375:50vw mob390:50vw desk:300px"
          loading="lazy"
          class="w-full h-full object-cover transition-opacity duration-300"
          :class="{ 'opacity-70': hovering }"
          :alt="asset.name"
          @error="imageError = true"
        />
        <img
          v-else-if="!isNorceUrl && !imageError"
          :src="asset.thumbnailUrl"
          loading="lazy"
          class="w-full h-full object-cover transition-opacity duration-300"
          :class="{ 'opacity-70': hovering }"
          :alt="asset.name"
          @error="imageError = true"
        >
        <div
          v-else
          class="w-full h-full flex items-center justify-center"
        >
          <img
            src="/icons/no-image.svg"
            class="w-32 h-32 opacity-30"
            alt=""
          >
        </div>
      </template>
      <template v-else>
        <div class="w-full h-full flex items-center justify-center bg-lightest border-b border-border-subtle">
          <img
            :src="documentIcon"
            class="w-48 h-48 opacity-40"
            alt=""
          >
        </div>
      </template>

      <!-- Hover actions -->
      <transition name="fade">
        <div
          v-if="hovering"
          class="absolute inset-0 flex items-center justify-center gap-8 bg-transparentBlack bg-opacity-30"
        >
          <!-- Images: open preview modal -->
          <button
            v-if="asset.mediaType === 'Images'"
            class="btn btn--secondary !bg-lightest !text-darkest h-36 !px-12 type-xs"
            @click.stop="$emit('preview', asset)"
          >
            {{ $t('mediaBank.card.preview') }}
          </button>
          <!-- Documents: open in new tab (only PDFs, the browser cannot show a zip) -->
          <a
            v-else-if="isPdf"
            :href="asset.url"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn--secondary !bg-lightest !text-darkest h-36 !px-12 type-xs"
            @click.stop
          >
            {{ $t('mediaBank.card.open') }}
          </a>
          <button
            class="btn btn--secondary !bg-lightest !text-darkest h-36 !px-12 type-xs"
            @click.stop="$emit('download', asset)"
          >
            {{ $t('mediaBank.card.download') }}
          </button>
        </div>
      </transition>

      <!-- Select button -->
      <button
        class="absolute top-8 right-8 p-4 transition-opacity w-40 h-40 inline-flex items-center justify-center bg-bg-base border border-border-subtle"
        :class="{
        'opacity-100': isFavorite,
        'opacity-80 hover:opacity-100': !isFavorite
      }"
        :title="isFavorite ? $t('mediaBank.preview.removeSelected') : $t('mediaBank.preview.addSelected')"
        @click.stop="$emit('toggleFavorite', asset)"
      >
        <img
          :src="isFavorite ? '/icons/check.svg' : '/icons/plus.svg'"
          class="w-20 h-20"
          alt=""
        >
      </button>

      <!-- Product type label (images only) -->
      <span
        v-if="asset.productType && asset.mediaType === 'Images'"
        class="absolute bottom-8 right-8 inline-flex items-center justify-center h-20 px-6 type-headline-xs leading-single bg-bg-secondary text-text-subtle"
      >
        {{ productTypeLabel }}
      </span>
    </div>

    <!-- Card footer -->
    <div class="p-16">
      <!-- Documents: show document name as title -->
      <template v-if="asset.mediaType === 'Documents'">
        <div class="type-headline-sm leading-tight line-clamp-2 mb-4">
          {{ asset.name || asset.mediaSetTitle }}
        </div>
        <div class="type-xs text-text-subtle">
          {{ $t('mediaBank.card.type') }}: {{ asset.mediaSetTitle }}
        </div>
      </template>
      <!-- Images: show product name and article number -->
      <template v-else>
        <div class="type-headline-sm leading-tight line-clamp-1 mb-4">
          {{ asset.productName }}
        </div>
        <div class="type-xs text-text-subtle">
          {{ $t('mediaBank.card.article') }}: {{ asset.articleNumber }}
        </div>
        <div class="type-xs text-text-subtle mt-4">
          {{ $t('mediaBank.card.type') }}: {{ asset.mediaSetTitle }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMediaBankStore } from '~/store/mediaBank';
import { useGlobalContentStore } from '~/store/globalContent';
import type { MediaAsset } from '#constants/types/mediaBank';

const { $t } = useNuxtApp();
const mediaBankStore = useMediaBankStore();
const globalContentStore = useGlobalContentStore();

const props = defineProps<{
  asset: MediaAsset;
}>();

defineEmits<{
  (e: 'preview', asset: MediaAsset): void;
  (e: 'download', asset: MediaAsset): void;
  (e: 'toggleFavorite', asset: MediaAsset): void;
}>();

const hovering = ref(false);
const imageError = ref(false);

const productTypeLabel = computed(() =>
  props.asset.productType ? globalContentStore.getFilterOption('categoryFilters.' + props.asset.productType).name : ''
);

const isNorceUrl = computed(() => props.asset.thumbnailUrl.includes('cdn-norce.tech'));
const isPdf = computed(() => props.asset.mimeType === 'application/pdf' || props.asset.fileType === '.pdf');
const isZip = computed(() => props.asset.mimeType.includes('zip') || props.asset.fileType === '.zip');
const documentIcon = computed(() => (isZip.value ? '/icons/file-zip.svg' : '/icons/file-pdf.svg'));
const isFavorite = computed(() => mediaBankStore.isFavorite(props.asset.id));
</script>

<style scoped lang="postcss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
